import { useEffect, useRef, useState } from 'react'
import { reportError } from '@mc/next-newrelic'
import {
  hasSubmitted,
  rememberSubmission,
  type Submission,
} from './submissionStorage'

export type SubmissionStatus =
  | { kind: 'idle' }
  | { kind: 'alreadyRequested' }
  | { kind: 'success'; email: string }
  | { kind: 'error' }

type UseRememberedSubmissionOptions = {
  submission: Submission
  slug: string
  emailFieldName: string
  request: (request: { slug: string; email: string }) => Promise<unknown>
  failureMessage: string
  /** Re-reads the remembered request whenever this turns true, e.g. when a modal opens. */
  active?: boolean
}

export const useRememberedSubmission = ({
  submission,
  slug,
  emailFieldName,
  request,
  failureMessage,
  active = true,
}: UseRememberedSubmissionOptions) => {
  const [status, setStatus] = useState<SubmissionStatus>({ kind: 'idle' })
  const headingRef = useRef<HTMLHeadingElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const focusTarget = useRef<'heading' | 'email' | null>(null)

  // Read after mount so server and client render the same initial markup.
  useEffect(() => {
    if (active) {
      setStatus(
        hasSubmitted(submission, slug)
          ? { kind: 'alreadyRequested' }
          : { kind: 'idle' },
      )
    }
  }, [active, submission, slug])

  useEffect(() => {
    if (focusTarget.current === 'heading') {
      headingRef.current?.focus()
    } else if (focusTarget.current === 'email') {
      formRef.current
        ?.querySelector<HTMLInputElement>(`input[name="${emailFieldName}"]`)
        ?.focus()
    }
    focusTarget.current = null
  }, [status, emailFieldName])

  const submitEmail = async (email: string, onSuccess: () => void) => {
    try {
      await request({ slug, email })
    } catch (error) {
      reportError(error, { message: failureMessage, slug })
      setStatus({ kind: 'error' })
      return
    }
    onSuccess()
    rememberSubmission(submission, slug)
    focusTarget.current = 'heading'
    setStatus({ kind: 'success', email })
  }

  const showForm = () => {
    focusTarget.current = 'email'
    setStatus({ kind: 'idle' })
  }

  return { status, headingRef, formRef, submitEmail, showForm }
}
